import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { leadsApi, ApiError } from "@/api";
import type { Lead, LeadStage } from "@/types";
import { LEAD_PIPELINE_STAGES, LEAD_OFFRAMP_STAGES } from "@/types";
import { Pill, Spinner, SelectField } from "@/components/ui";
import { effectiveStage, stageLabel, stageTone, leadName, matchesQuery, dueInfo, isSet, isOpenLead } from "@/lib/leads";
import { formatDateShort } from "@/lib/format";
import { LeadDrawer } from "./LeadDrawer";
import { AddLeadDrawer } from "./AddLeadDrawer";
import "./leads.css"; // the drawers style from here, not their own import
import "./leads-list.css";

const ALL_STAGES: readonly LeadStage[] = [...LEAD_PIPELINE_STAGES, ...LEAD_OFFRAMP_STAGES];

type LeadsView = "all" | "open";

const VIEWS: { value: LeadsView; label: string }[] = [
  { value: "all", label: "All" },
  { value: "open", label: "Open only" },
];

// Survives list → drawer → away → back (the list unmounts on navigation) but
// resets to "All" in a fresh tab — same convention as the quotes list.
const VIEW_STORAGE_KEY = "leads-list.view";

function storedView(): LeadsView {
  return sessionStorage.getItem(VIEW_STORAGE_KEY) === "open" ? "open" : "all";
}

export function LeadsList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [stageFilter, setStageFilter] = useState<"" | LeadStage>("");
  const [view, setView] = useState<LeadsView>(storedView);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showAdd, setShowAdd] = useState(false);

  function selectView(v: LeadsView) {
    setView(v);
    sessionStorage.setItem(VIEW_STORAGE_KEY, v);
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    leadsApi
      .listLeads({
        include_archived: view === "all",
        per_page: 200,
      })
      .then((res) => {
        if (!cancelled) setLeads(res.items ?? []);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load leads.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [view]);

  // The header's "New lead" routes here with { create: true } — open the
  // Add-lead drawer and strip the flag so a refresh/back doesn't re-open it.
  useEffect(() => {
    if ((location.state as { create?: boolean } | null)?.create) {
      setShowAdd(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  // The server excludes archived rows on the open view; off-ramp stages it
  // still returns, so those are dropped here. Search is client-side over the
  // fetched rows, same matcher as the pipeline board. The stage filter is
  // client-side too, on effectiveStage — the server can't express that legacy
  // consulted rows belong under Qualified.
  const visible = useMemo(
    () =>
      (view === "open" ? leads.filter(isOpenLead) : leads)
        .filter((l) => !stageFilter || effectiveStage(l.stage) === stageFilter)
        .filter((l) => matchesQuery(l, q)),
    [leads, view, stageFilter, q],
  );

  function mergeLead(updated: Lead) {
    setLeads((ls) => ls.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
  }

  // New manual lead: drop it into the list and open the detail drawer so staff
  // can immediately add the brief / record a consultation / create the quote.
  function handleLeadCreated(lead: Lead) {
    setShowAdd(false);
    if (!lead || lead.id == null) return; // shape guard — never render a row without an id
    setLeads((ls) => [lead, ...ls]);
    setSelectedId(lead.id);
  }

  function openLead(id: number) {
    setSelectedId(id);
  }

  const selected = selectedId != null ? leads.find((l) => l.id === selectedId) ?? null : null;

  return (
    <section className="leads-list">
      <header className="leads-list__head">
        <div>
          <h1>Leads</h1>
          <p className="leads-list__count">{visible.length} shown</p>
        </div>
      </header>

      <div className="leads-list__filters">
        <SelectField
          label="Stage"
          labelHidden
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value as "" | LeadStage)}
        >
          <option value="">All stages</option>
          {ALL_STAGES.map((s) => (
            <option key={s} value={s}>{stageLabel(s)}</option>
          ))}
        </SelectField>
        <div className="leads-list__seg" role="tablist" aria-label="Open filter">
          {VIEWS.map((v) => (
            <button
              key={v.value}
              type="button"
              role="tab"
              aria-selected={view === v.value}
              className={`leads-list__seg-btn${view === v.value ? " is-on" : ""}`}
              onClick={() => selectView(v.value)}
            >
              {v.label}
            </button>
          ))}
        </div>
        <input
          className="leads__search leads-list__search"
          type="search"
          placeholder="Search name, email, company, postcode…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {error ? <p className="leads-list__error" role="alert">{error}</p> : null}

      {loading ? (
        <div className="leads-list__loading"><Spinner /></div>
      ) : visible.length === 0 ? (
        <p className="leads-list__empty">No leads match these filters.</p>
      ) : (
        <div className="leads-table" role="table" aria-label="Leads">
          <div className="leads-table__head" role="row">
            <span role="columnheader">Lead</span>
            <span role="columnheader">Project</span>
            <span role="columnheader">Source</span>
            <span role="columnheader">Stage</span>
            <span role="columnheader">Postcode</span>
            <span role="columnheader">Next action</span>
            <span role="columnheader">Created</span>
          </div>
          {visible.map((lead) => {
            const due = dueInfo(lead.next_action_at);
            const attribution = [lead.source, lead.campaign].filter(Boolean).join(" · ");
            return (
              <div
                key={lead.id}
                className="leads-table__row"
                role="row"
                tabIndex={0}
                onClick={() => openLead(lead.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openLead(lead.id);
                  }
                }}
              >
                <span role="cell" data-label="Lead" className="leads-table__name">
                  {leadName(lead)}
                </span>
                <span role="cell" data-label="Project" className="leads-table__desc">
                  {lead.project_description || "—"}
                </span>
                <span
                  role="cell"
                  data-label="Source"
                  className="leads-table__source"
                  title={attribution || undefined}
                >
                  {attribution || "—"}
                </span>
                <span role="cell" data-label="Stage">
                  {/* Won leads (archived by quote provisioning) keep a stale
                      stage — label them Won, matching the board's Won column. */}
                  {isSet(lead.archived_at) ? (
                    <Pill tone="success">Won</Pill>
                  ) : (
                    <Pill tone={stageTone(lead.stage)}>{stageLabel(lead.stage)}</Pill>
                  )}
                </span>
                <span role="cell" data-label="Postcode" className="numeric">
                  {lead.site_postcode || "—"}
                </span>
                <span role="cell" data-label="Next action">
                  {due ? (
                    <span className={`lead-card__due lead-card__due--${due.tone}`}>{due.label}</span>
                  ) : (
                    "—"
                  )}
                </span>
                <span role="cell" data-label="Created" className="numeric">
                  {formatDateShort(lead.created_at)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {selected ? (
        <LeadDrawer
          lead={selected}
          onClose={() => setSelectedId(null)}
          onChanged={mergeLead}
        />
      ) : null}

      {showAdd ? (
        <AddLeadDrawer onCreated={handleLeadCreated} onClose={() => setShowAdd(false)} />
      ) : null}
    </section>
  );
}
